"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import {
  Search,
  User,
  Menu,
  X,
  Bell,
  ChevronDown,
  LogIn,
  Wallet,
  Settings,
  LogOut,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { GlobalSearch } from "@/components/layout/global-search";
import { CategoriesMegamenu } from "@/components/layout/categories-megamenu";
import { useFlags } from "@/lib/flags/client";
import { useNavTabs } from "@/lib/navigation/use-nav-tabs";
import type { MegamenuCategory } from "@/lib/categories-menu";

interface NavbarProps {
  user?: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
    level?: string;
  } | null;
  /** Top-level categories used by the "Categorías" megamenu. Empty array
   *  collapses the megamenu to a regular link (no panel). */
  categories?: readonly MegamenuCategory[];
  /** Optional ad slot rendered inside the megamenu (admin-configurable). */
  megamenuAd?: React.ReactNode;
  megamenuVerticalAds?: readonly React.ReactNode[];
  megamenuHorizontalAds?: readonly React.ReactNode[];
}

function isActive(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`) || pathname.startsWith(`${href}?`);
}

export function Navbar({
  user,
  categories = [],
  megamenuAd,
  megamenuVerticalAds,
  megamenuHorizontalAds,
}: NavbarProps) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const flags = useFlags();
  const tabs = useNavTabs();
  const desktopTabs = tabs.filter((t) => t.visibility !== "mobile");
  const mobileTabs = tabs.filter((t) => t.visibility !== "desktop");

  const cashbackOn = flags["cashback"] !== false;
  const priceAlertsOn = flags["products.priceAlerts"] !== false && flags["products"] !== false;
  const referralsOn = flags["referrals"] !== false;

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur-sm supports-backdrop-filter:bg-background/80 shadow-xs">
      <div className="container-app">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link href="/" aria-label="SuCom — Inicio" className="flex items-center gap-2.5 font-bold shrink-0">
            <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-9 h-9 text-primary">
              <path d="M 35 26 A 15 15 0 0 1 65 26" stroke="currentColor" strokeWidth="8" strokeLinecap="round" />
              <path d="M 25 38 L 75 38 L 84 86 L 16 86 Z" stroke="currentColor" strokeWidth="8" strokeLinejoin="round" />
              <text x="50" y="75" fontSize="44" fontWeight="bold" fill="currentColor" textAnchor="middle" fontFamily="sans-serif">S</text>
            </svg>
            <span className="hidden sm:block text-primary text-3xl font-semibold tracking-tight">
              SuCom
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Navegación principal">
            {desktopTabs.map(({ id, href, label, icon: Icon, badge }) => {
              const active = isActive(pathname, href);
              const className = cn(
                "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative",
                active
                  ? "text-primary bg-primary/10"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              );

              // The "Categorías" tab opens a megamenu on hover/focus.
              // The trigger itself is still a real link to /categorias —
              // clicking it navigates; hovering reveals the panel.
              if (id === "categorias") {
                return (
                  <CategoriesMegamenu
                    key={id}
                    categories={categories}
                    adSlot={megamenuAd}
                    verticalAdSlots={megamenuVerticalAds}
                    horizontalAdSlots={megamenuHorizontalAds}
                    href={href}
                    active={active}
                    triggerClassName={className}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                    {badge && (
                      <Badge variant="hot" className="text-[10px] px-1.5 py-0 h-4 absolute -top-1 -right-1">
                        {badge}
                      </Badge>
                    )}
                  </CategoriesMegamenu>
                );
              }

              return (
                <Link
                  key={id}
                  href={href}
                  aria-current={active ? "page" : undefined}
                  className={className}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                  {badge && (
                    <Badge variant="hot" className="text-[10px] px-1.5 py-0 h-4 absolute -top-1 -right-1">
                      {badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setSearchOpen(!searchOpen)}
              className="text-muted-foreground"
              aria-label="Buscar"
              aria-expanded={searchOpen}
            >
              <Search className="w-5 h-5" />
            </Button>

            {user && (
              <Button asChild variant="ghost" size="icon-sm" className="relative text-muted-foreground">
                <Link href="/perfil" aria-label="Notificaciones">
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                </Link>
              </Button>
            )}

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted transition-colors"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user.image ?? ""} />
                    <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                      {user.name?.slice(0, 2).toUpperCase() ?? "US"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <p className="text-xs font-semibold leading-none">{user.name?.split(" ")[0]}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{user.level ?? "Básico"}</p>
                  </div>
                  <ChevronDown className="w-3 h-3 text-muted-foreground hidden sm:block" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-background border rounded-xl shadow-lg py-1 z-50" role="menu">
                    <Link
                      href="/perfil"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="w-4 h-4" /> Mi Perfil
                    </Link>
                    {cashbackOn && (
                      <Link
                        href="/cashback/wallet"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Wallet className="w-4 h-4" /> Mi Wallet
                      </Link>
                    )}
                    {priceAlertsOn && (
                      <Link
                        href="/perfil/alertas"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Bell className="w-4 h-4" /> Alertas de Precio
                      </Link>
                    )}
                    {referralsOn && (
                      <Link
                        href="/perfil/referidos"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Star className="w-4 h-4" /> Referidos
                      </Link>
                    )}
                    <hr className="my-1 border-border" />
                    <Link
                      href="/perfil/configuracion"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4" /> Configuración
                    </Link>
                    <form action="/api/auth/signout" method="POST">
                      <button className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                        <LogOut className="w-4 h-4" /> Cerrar Sesión
                      </button>
                    </form>
                  </div>
                )}
              </div>
            ) : (
              <Button asChild size="sm">
                <Link href="/auth/login" aria-label="Ingresar">
                  <LogIn className="w-4 h-4" />
                  <span className="hidden sm:inline">Ingresar</span>
                </Link>
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon-sm"
              className="lg:hidden text-muted-foreground"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Abrir menú"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {searchOpen && (
          <div className="pb-3 animate-fade-in">
            <GlobalSearch autoFocus onClose={() => setSearchOpen(false)} />
          </div>
        )}

        {mobileOpen && (
          <nav className="lg:hidden pb-4 animate-fade-in" aria-label="Navegación móvil">
            <div className="grid grid-cols-2 gap-2">
              {mobileTabs.map(({ id, href, label, icon: Icon, badge }) => {
                const active = isActive(pathname, href);
                return (
                  <Link
                    key={id}
                    href={href}
                    onClick={() => setMobileOpen(false)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors relative",
                      active
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                    {badge && <Badge variant="hot" className="text-[10px] px-1 h-3.5">{badge}</Badge>}
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
